
import { jitsiLocalStorage } from '@jitsi/js-utils';
import React, { PureComponent } from 'react';
import type { Dispatch } from 'redux';

import { createToolbarEvent, sendAnalytics } from '../../analytics';
import { setAudioOnly } from '../../base/audio-only';
import { translate } from '../../base/i18n';
import { connect } from '../../base/redux';
import { isScreenVideoShared } from '../../base/tracks';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../../notifications';
import { setPreferredVideoQuality } from '../actions';
import { VIDEO_QUALITY_LEVELS } from '../constants';
import logger from '../logger';

const {
    AUTO,
    HIGH,
    STANDARD,
    LOW
} = VIDEO_QUALITY_LEVELS;

/**
 * Creates an analytics event for a press of one of the buttons in the video
 * quality dialog.
 *
 * @param {string} quality - The quality which was selected.
 * @returns {Object} The event in a format suitable for sending via
 *      sendAnalytics.
 */
const createEvent = function(quality) {
    return createToolbarEvent(
        'video.quality',
        {
            quality
        });
};

/**
 * The type of the React {@code Component} props of {@link VideoQualitySlider}.
 */
type Props = {

    /**
     * Whether or not the conference is in audio only mode.
     */
    _audioOnly: Boolean,

    /**
     * The custom resolution (one of the VIDEO_QUALITY_LEVELS values, e.g. 240/360/480/720) locked by config
     * (customResolution + interfaceConfig.applyMeetingSettings). When set, the slider is read-only and a
     * notice is shown explaining the lock. undefined means the user is free to pick.
     */
    _customResolution: ?number,

    /**
     * The localized label for the locked quality (e.g. "Standard definition"). Empty when not locked.
     */
    _lockedQualityLabel: string,

    /**
     * Whether or not the conference is in peer to peer mode.
     */
    _p2p: Boolean,

    /**
     * The currently configured maximum quality resolution to be sent and
     * received from the remote participants.
     */
    _sendrecvVideoQuality: Number,

    /**
     * Whether or not screen sharing is currently active.
     */
    _screenSharingActive: Boolean,

    /**
     * Invoked to request toggling of audio only mode.
     */
    dispatch: Dispatch<any>,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function
};

/**
 * Implements a React {@link Component} which displays a slider for selecting a
 * new receive video quality.
 *
 * @augments Component
 */
class VideoQualitySlider extends PureComponent<Props> {
    _sliderOptions: Array<Object>;

    /**
     * Initializes a new {@code VideoQualitySlider} instance.
     *
     * @param {Object} props - The read-only React Component props with which
     * the new instance is to be initialized.
     */
    constructor(props) {
        super(props);

        // Bind event handlers so they are only bound once for every instance.
        this._enableAudioOnly = this._enableAudioOnly.bind(this);
        this._enableHighDefinition = this._enableHighDefinition.bind(this);
        this._enableLowDefinition = this._enableLowDefinition.bind(this);
        this._enableStandardDefinition
            = this._enableStandardDefinition.bind(this);
        this._enableAuto = this._enableAuto.bind(this);
        this._onSliderChange = this._onSliderChange.bind(this);

        /**
         * An array of configuration options for displaying a choice in the
         * input. The onSelect callback will be invoked when the option is
         * selected and videoQuality helps determine which choice matches with
         * the currently active quality level.
         *
         * @private
         * @type {Object[]}
         */
        this._sliderOptions = [
            {
                audioOnly: true,
                onSelect: this._enableAudioOnly,
                textKey: 'audioOnly.audioOnly'
            },
            {
                onSelect: this._enableLowDefinition,
                textKey: 'videoStatus.lowDefinition',
                videoQuality: LOW
            },
            {
                onSelect: this._enableStandardDefinition,
                textKey: 'videoStatus.standardDefinition',
                videoQuality: STANDARD
            },
            {
                onSelect: this._enableHighDefinition,
                textKey: 'videoStatus.highDefinition',
                videoQuality: HIGH
            },
            {
                onSelect: this._enableAuto,
                textKey: 'videoStatus.auto',
                videoQuality: AUTO
            }
        ];
    }


    /**
     * React lifecycle method invoked after the component is mounted.
     */
    componentDidMount() {
        const { checked } = this.props;
        const persistedPrefferedVideoQuality = jitsiLocalStorage.getItem('features/video-quality-persistent-storage');
        let videoQuality;

        try {
            videoQuality = persistedPrefferedVideoQuality ? JSON.parse(persistedPrefferedVideoQuality) : undefined;
        } catch {
            videoQuality = undefined;
        }

        if (checked && typeof videoQuality?.persistedPrefferedVideoQuality !== 'undefined') {
            this._setPreferredVideoQuality(videoQuality.persistedPrefferedVideoQuality);
        }
    }


    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { t, _customResolution } = this.props;
        const activeSliderOption = this._mapCurrentQualityToSliderValue();
        const isLocked = typeof _customResolution === 'number';

        return (
            <div className = { 'video-quality-dialog' }>
                <h3 className = 'video-quality-dialog-title'>
                    { t('videoStatus.callQuality') }
                </h3>
                <div className = 'video-quality-dialog-contents'>
                    { isLocked && this._renderLockedNotice() }
                    <div className = 'video-quality-dialog-slider-container'>
                        { /* FIXME: onChange and onMouseUp are both used for
                           * compatibility with IE11. This workaround can be
                           * removed after upgrading to React 16.
                           */ }
                        <input
                            className = 'video-quality-dialog-slider'
                            disabled = { isLocked }
                            max = { this._sliderOptions.length - 1 }
                            min = '0'
                            onChange = { this._onSliderChange }
                            onMouseUp = { this._onSliderChange }
                            step = '1'
                            type = 'range'
                            value
                                = { activeSliderOption } />

                    </div>
                    <div
                        className = { `video-quality-dialog-labels${isLocked ? ' locked' : ''}` }>
                        { this._createLabels(activeSliderOption) }
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Renders the notice shown above the slider when the quality is locked by customResolution.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderLockedNotice() {
        const { t, _lockedQualityLabel } = this.props;

        // `_lockedQualityLabel` is an i18n key (e.g. "videoStatus.standardDefinition"); i18next does not
        // recursively translate nested keys inside an interpolation placeholder, so resolve it first and
        // hand the localized text as the `quality` argument. Otherwise the user sees the raw key in the
        // notice ("...locked to videoStatus.standardDefinition...").
        const qualityText = _lockedQualityLabel ? t(_lockedQualityLabel) : '';

        return (
            <div
                aria-live = 'polite'
                className = 'video-quality-dialog-locked-notice'
                role = 'status'>
                { t('videoStatus.videoQualityLocked', { quality: qualityText }) }
            </div>
        );
    }

    /**
     * Creates React Elements to display mock tick marks with associated labels.
     *
     * @param {number} activeLabelIndex - Which of the sliderOptions should
     * display as currently active.
     * @private
     * @returns {ReactElement[]}
     */
    _createLabels(activeLabelIndex) {
        const labelsCount = this._sliderOptions.length;
        const maxWidthOfLabel = `${100 / (labelsCount + 1)}%`;

        return this._sliderOptions.map((sliderOption, index) => {
            const style = {
                maxWidth: maxWidthOfLabel,
                left: `${(index * 100) / (labelsCount - 1)}%`
            };

            const isActiveClass = activeLabelIndex === index ? 'active' : '';
            const className
                = `video-quality-dialog-label-container ${isActiveClass}`;

            return (
                <div
                    className = { className }
                    key = { index }
                    style = { style }>
                    <div className = 'video-quality-dialog-label'>
                        { this.props.t(sliderOption.textKey) }
                    </div>
                </div>
            );
        });
    }

    _enableAudioOnly: () => void;

    /**
     * Dispatches an action to enable audio only mode.
     *
     * @private
     * @returns {void}
     */
    _enableAudioOnly() {
        sendAnalytics(createEvent('audio.only'));
        logger.log('Video quality: audio only enabled');
        this.props.dispatch(setAudioOnly(true));
    }

    _enableHighDefinition: () => void;

    /**
     * Handles the action of the high definition video being selected.
     * Dispatches an action to receive high quality video from remote
     * participants.
     *
     * @private
     * @returns {void}
     */
    _enableHighDefinition() {
        sendAnalytics(createEvent('high'));
        logger.log('Video quality: high enabled');
        this._setPreferredVideoQuality(HIGH);
    }

    _enableLowDefinition: () => void;

    /**
     * Dispatches an action to receive low quality video from remote
     * participants.
     *
     * @private
     * @returns {void}
     */
    _enableLowDefinition() {
        sendAnalytics(createEvent('low'));
        logger.log('Video quality: low enabled');
        this._setPreferredVideoQuality(LOW);
    }

    _enableStandardDefinition: () => void;

    /**
     * Dispatches an action to receive standard quality video from remote
     * participants.
     *
     * @private
     * @returns {void}
     */
    _enableStandardDefinition() {
        sendAnalytics(createEvent('standard'));
        logger.log('Video quality: standard enabled');
        this._setPreferredVideoQuality(STANDARD);
    }

    _enableAuto: () => void;

    /**
     * Handles the action of the auto video quality being selected.
     * Dispatches an action to receive ultra HD quality video from remote
     * participants and saves the preference as 'AUTO'.
     *
     * @private
     * @returns {void}
     */
    _enableAuto() {
        sendAnalytics(createEvent('auto (ultra) high'));
        logger.log('Video quality: auto (ULTRA) enabled');
        this._setPreferredVideoQuality(AUTO);
    }

    /**
     * Matches the current video quality state with corresponding index of the
     * component's slider options.
     *
     * @private
     * @returns {void}
     */
    _mapCurrentQualityToSliderValue() {
        const { _audioOnly, _customResolution, _sendrecvVideoQuality } = this.props;
        const { _sliderOptions } = this;

        // When the meeting is locked to a specific resolution via config, the slider must visibly sit on
        // that option regardless of any stale preferredVideoQuality in Redux. MEDIUM is not one of the
        // selectable slider stops; it is consumed by the middleware as a receiver cap but the slider
        // itself only offers LOW/STANDARD/HIGH/AUTO, so we snap MEDIUM to STANDARD for visual coherence.
        if (typeof _customResolution === 'number') {
            const resolutionToHighlight
                = _customResolution === VIDEO_QUALITY_LEVELS.MEDIUM
                    ? VIDEO_QUALITY_LEVELS.STANDARD
                    : _customResolution;
            const lockedIndex = _sliderOptions.findIndex(
                o => typeof o.videoQuality === 'number' && o.videoQuality === resolutionToHighlight);

            if (lockedIndex !== -1) {
                return lockedIndex;
            }
        }

        if (_audioOnly) {
            const audioOnlyOption = _sliderOptions.find(
                ({ audioOnly }) => audioOnly);

            return _sliderOptions.indexOf(audioOnlyOption);
        }

        for (let i = 0; i < _sliderOptions.length; i++) {
            if (_sliderOptions[i].videoQuality >= _sendrecvVideoQuality) {
                return i;
            }
        }

        return -1;
    }

    _onSliderChange: () => void;

    /**
     * Invokes a callback when the selected video quality changes.
     *
     * @param {Object} event - The slider's change event.
     * @private
     * @returns {void}
     */
    _onSliderChange(event) {
        const {
            _audioOnly,
            _customResolution,
            _sendrecvVideoQuality,
            _screenSharingActive
        } = this.props;
        const {
            audioOnly,
            onSelect,
            videoQuality
        } = this._sliderOptions[event.target.value];

        // Quality is locked by config. Even though the input is disabled, guard here too so the lock holds
        // even if a programmatic dispatch path is wired in later.
        if (typeof _customResolution === 'number') {
            return;
        }

        // Prevent enabling audio-only mode when screen sharing is active
        if (audioOnly && _screenSharingActive) {
            this.props.dispatch(showNotification({
                titleKey: 'notify.lowBandwidthDisabledScreenShareTitle',
                descriptionKey: 'notify.lowBandwidthDisabledScreenShareDescription'
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));

            return;
        }

        // Take no action if the newly chosen option does not change audio only
        // or video quality state.
        if ((_audioOnly && audioOnly)
            || (!_audioOnly && videoQuality === _sendrecvVideoQuality)) {
            return;
        }

        onSelect();
    }

    /**
     * Helper for changing the preferred maximum video quality to receive and
     * disable audio only.
     *
     * @param {number} qualityLevel - The new maximum video quality. Should be
     * a value enumerated in {@code VIDEO_QUALITY_LEVELS}.
     * @private
     * @returns {void}
     */
    _setPreferredVideoQuality(qualityLevel) {
        // Defense-in-depth: when customResolution locks the meeting, never dispatch a new preference.
        // componentDidMount also routes through this helper when restoring a persisted value, so the lock
        // also stops the slider from snapping back to a saved user pick on open.
        if (typeof this.props._customResolution === 'number') {
            return;
        }

        // The slider is the one path where the quality is genuinely the user's choice, so it is the only caller
        // that passes userInitiated. That is what lets it outrank the participant-count cap.
        this.props.dispatch(setPreferredVideoQuality(qualityLevel, true));

        const isSavePreferenceEnabled = this.props.checked
            || jitsiLocalStorage.getItem('features/video-quality-checked') === 'true';

        if (isSavePreferenceEnabled) {
            jitsiLocalStorage.setItem(
                'features/video-quality-persistent-storage',
                JSON.stringify({
                    persistedPrefferedVideoQuality: qualityLevel
                })
            );
        }

        if (this.props._audioOnly) {
            this.props.dispatch(setAudioOnly(false));
        }
    }
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code VideoQualitySlider} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const { enabled: audioOnly } = state['features/base/audio-only'];
    const { p2p } = state['features/base/conference'];
    const { preferredVideoQuality } = state['features/video-quality'];
    const config = state['features/base/config'];
    const screenSharingActive = isScreenVideoShared(state);

    // The slider mirrors the same gating the middleware uses to dispatch customResolution, so what the
    // user sees here is exactly what is in force. When customResolution is missing or not one of the four
    // selectable levels, the slider behaves normally. References the module-level AUTO/HIGH/STANDARD/LOW
    // constants directly to avoid shadowing them inside this function.
    const { MEDIUM } = VIDEO_QUALITY_LEVELS;
    const isLockableContext = config?.interfaceConfig?.applyMeetingSettings === true && !config?.iAmRecorder;
    const rawCustomResolution = isLockableContext ? config?.customResolution : undefined;
    const customResolution
        = typeof rawCustomResolution === 'number' && Number.isInteger(rawCustomResolution)
            ? rawCustomResolution
            : undefined;
    let lockedQualityLabel = '';

    if (typeof customResolution === 'number') {
        if (customResolution === LOW || customResolution === MEDIUM) {
            lockedQualityLabel = 'videoStatus.lowDefinition';
        } else if (customResolution === STANDARD) {
            lockedQualityLabel = 'videoStatus.standardDefinition';
        } else if (customResolution === HIGH) {
            lockedQualityLabel = 'videoStatus.highDefinition';
        }
    }

    return {
        _audioOnly: audioOnly,
        _customResolution: customResolution,
        _lockedQualityLabel: lockedQualityLabel,
        _p2p: p2p,
        _sendrecvVideoQuality: preferredVideoQuality,
        _screenSharingActive: Boolean(screenSharingActive)
    };
}

export default translate(connect(_mapStateToProps)(VideoQualitySlider));

